import { TAG } from "./projectTags";

const projectEntry = (
  tags,
  sections,
  recruiterSummary,
  overview,
  overviewLead = null,
) => ({
  recruiterSummary,
  overview,
  overviewLead,
  tags,
  sections,
});

export const projectTOC = {
  aestheticGallery: projectEntry(
    [TAG.REACT, TAG.BOOTSTRAP, TAG.UX_UI, TAG.LO_FI_WIREFRAMES, TAG.MID_FI_WIREFRAMES, TAG.AFFINITY_DESIGNER, TAG.PROCREATE],
    [
      { id: "quick-summary", title: "Quick Summary" },
      { id: "overview", title: "Project Overview" },
      { id: "projectlinks", title: "Project Links" },
      { id: "research", title: "Research & Inspiration" },
      { id: "artwork", title: "Artwork Creation" },
      { id: "gallery", title: "Gallery Design & Project Evolution" },
      { id: "features", title: "Features & Implementation" },
      { id: "tools", title: "Tools & Technologies Used" },
      { id: "challenges", title: "Challenges & Solutions" },
      { id: "outcomes", title: "Outcomes & Impact" },
      { id: "conclusion", title: "Conclusion" },
    ],
    "Served as designer and front-end developer on a personal art portfolio spanning 2023–2025. Created 20+ original artworks in Procreate and Affinity Designer, then evolved the project from a static PDF into a fully interactive React gallery with category filtering and lightbox viewing.\n\nOwned the full creative pipeline,visual identity, illustration, responsive layout, and vaporwave branding across desktop and mobile. Demonstrates end-to-end delivery of a cohesive creative product, not just a showcase of individual pieces.",
    "The 80s Aesthetic Art Gallery is an interactive React portfolio showcasing 20+ original vaporwave-inspired digital artworks, with responsive browsing, category filtering, and lightbox viewing."
  ),
  soundArt: projectEntry(
    [TAG.REACT, TAG.BOOTSTRAP, TAG.UX_UI, TAG.MID_FI_WIREFRAMES, TAG.HI_FI_WIREFRAMES],
    [
      { id: "quick-summary", title: "Quick Summary" },
      { id: "overview", title: "Project Overview" },
      { id: "projectlinks", title: "Project Links" },
      { id: "problem", title: "Problem Statement" },
      { id: "rebuild", title: "Static vs. ReactJS" },
      { id: "research", title: "Research & Insights" },
      { id: "design", title: "Design Process" },
      { id: "features", title: "Features & Implementation" },
      { id: "tools", title: "Tools & Technologies Used" },
      { id: "testing", title: "Testing & Quality Assurance" },
      { id: "challenges", title: "Challenges & Solutions" },
      { id: "future", title: "Next Steps & Future Enhancements" },
      { id: "outcomes", title: "Outcomes & Impact" },
      { id: "conclusion", title: "Conclusion" },
    ],
    "Led UX/UI design and React development across two phases,an initial static build in 2023 and a full SPA rebuild in 2025. Designed an editorial-style music discovery experience to help listeners find emerging artists beyond mainstream playlists.\n\nRebuilt the platform with React Router and Bootstrap, scaling to 50+ dynamic artist profiles with search, filters, and rich profile pages. Ran usability testing and iterative improvements, documenting the full design-to-development journey in a detailed case study.",
    "SoundArt is a music discovery platform built with React that helps listeners find emerging artists through an editorial-style interface, with search, filters, and dynamic artist profiles."
  ),
  customPCBuilder: projectEntry(
    [TAG.UX_UI, TAG.FIGMA, TAG.MID_FI_WIREFRAMES, TAG.HI_FI_WIREFRAMES, TAG.JAVASCRIPT, TAG.BOOTSTRAP],
    [
      { id: "quick-summary", title: "Quick Summary" },
      { id: "overview", title: "Project Overview" },
      { id: "projectlinks", title: "Project Links" },
      { id: "design", title: "Design & Development Process" },
      { id: "features", title: "Features & Implementation" },
      { id: "tools", title: "Tools & Technologies Used" },
      { id: "challenges", title: "Challenges & Solutions" },
      { id: "outcomes", title: "Outcomes & Impact" },
      { id: "conclusion", title: "Conclusion" },
    ],
    "Worked as UX/UI designer and front-end developer on a guided PC configuration tool integrated into an existing e-commerce platform. Addressed a real business problem: shoppers selecting incompatible or incomplete components without visibility into their build.\n\nDesigned a multi-phase flow with mid-fi and hi-fi wireframes, then implemented step-by-step component selection, compatibility validation, real-time pricing, and a persistent build summary panel. Shipped a responsive tool that keeps users oriented at every step and reduces costly configuration errors.",
    "The Custom PC Builder is a guided configuration tool embedded in a live e-commerce store, walking shoppers through compatible component selection with real-time pricing, compatibility checks, and a persistent build summary."
  ),
  funzies: projectEntry(
    [TAG.REACT, TAG.TAILWIND, TAG.DAISYUI, TAG.UX_UI, TAG.HI_FI_WIREFRAMES],
    [
      { id: "quick-summary", title: "Quick Summary" },
      { id: "overview", title: "Project Overview" },
      { id: "projectlinks", title: "Project Links" },
      { id: "research", title: "Research & User Personas" },
      { id: "development", title: "Design & Development Process" },
      { id: "flowcharts", title: "Flowcharts & Information Architecture" },
      { id: "features", title: "Features & Implementation" },
      { id: "tools", title: "Tools & Technologies Used" },
      { id: "testing", title: "Testing & Quality Assurance" },
      { id: "challenges", title: "Challenges & Solutions" },
      { id: "future", title: "Future Enhancements" },
      { id: "outcomes", title: "Outcomes & Impact" },
      { id: "conclusion", title: "Conclusion" },
    ],
    "Led UX/UI design, front-end development, and full case-study documentation for a collector-focused e-commerce platform. Researched user needs around trust, discovery, and checkout confidence, then designed wishlists, product browsing, and feature-rich shopping journeys.\n\nRebuilt the storefront in 2026 as a modern React application with Vite, Tailwind CSS, DaisyUI, and a lightweight Express API for product CRUD and image uploads. Delivered a portfolio-ready marketplace optimized for usability, secure transactions, and future scalability.",
    "Funzies Collection is a collector-focused e-commerce storefront for toys, figures, and pop-culture goods, built as a React shop with product browsing, wishlists, secure checkout, and admin catalog management."
  ),
  icook: projectEntry(
    [TAG.UX_UI, TAG.MOBILE_DESIGN, TAG.LO_FI_WIREFRAMES, TAG.MID_FI_WIREFRAMES, TAG.HI_FI_WIREFRAMES, TAG.PROTOTYPING],
    [
      { id: "quick-summary", title: "Quick Summary" },
      { id: "overview", title: "Project Overview" },
      { id: "projectlinks", title: "Project Links" },
      { id: "problem", title: "Problem Statement" },
      { id: "research", title: "Research & User Flows" },
      { id: "features", title: "Features & Design" },
      { id: "tools", title: "Tools & Technologies Used" },
      { id: "usability", title: "Usability Testing & Improvements" },
      { id: "challenges", title: "Challenges & Solutions" },
      { id: "future", title: "Future Enhancements" },
      { id: "outcomes", title: "Outcomes & Impact" },
      { id: "conclusion", title: "Conclusion" },
    ],
    "Delivered UX research, mobile UI design, interaction flows, and Figma prototypes for a recipe and meal-planning concept, not a shipped native app. Targeted busy adults, beginner home cooks, and health-conscious users who need fast recipe discovery without the overwhelm of existing cooking apps.\n\nDesigned categorized recipes, integrated meal planning, automated grocery lists, and accessibility-conscious guidance throughout the experience. Produced mid-fi and hi-fi wireframes, user flows, and usability-tested improvements documented in a complete mobile UX case study.",
    "iCook is a mobile recipe and meal-planning concept designed and validated through Figma prototypes, covering categorized recipes, meal planning, grocery lists, and guided cooking flows for busy and beginner home cooks."
  ),
  lifeCanvas: projectEntry(
    [TAG.NEXT, TAG.TAILWIND, TAG.UX_UI, TAG.LO_FI_WIREFRAMES, TAG.HI_FI_WIREFRAMES],
    [
      { id: "quick-summary", title: "Quick Summary" },
      { id: "overview", title: "Project Overview" },
      { id: "projectlinks", title: "Project Links" },
      { id: "research", title: "Research & Insights" },
      { id: "personas", title: "User Personas, Flows & Information Architecture" },
      { id: "v1-v2", title: "V1 vs V2: Before & After" },
      { id: "design", title: "Design & Wireframes" },
      { id: "features", title: "Features & Implementation" },
      { id: "tools", title: "Tools & Technologies Used" },
      { id: "challenges", title: "Challenges & Solutions" },
      { id: "future", title: "Future Enhancements" },
      { id: "outcomes", title: "Outcomes & Impact" },
      { id: "conclusion", title: "Conclusion" },
    ],
    "Owned UX research, product design, and front-end implementation for a mobile-first productivity web app. Identified fragmentation across journaling, note-taking, and task apps as the core problem, then designed a calm, customizable experience that unifies planning, media, and inspiration in one space.\n\nEvolved the product from a Capacitor-based Angular/Ionic native prototype (V1) to a Next.js, React 19, and TypeScript redevelopment (V2) with browser-local storage and a deployable web demo. Produced lo-fi through hi-fi wireframes, user personas, competitive analysis, and a working build ready for cloud sync and native packaging as future enhancements.",
    "LifeCanvas is a mobile-first productivity web app that unifies journaling, notes, task planning, media, and daily inspiration in one calm, customizable workspace, redeveloped from an Angular/Ionic prototype to Next.js, React, and TypeScript."
  ),
  eversio: projectEntry(
    [TAG.REACT, TAG.NEST, TAG.BOOTSTRAP, TAG.UX_UI, TAG.HI_FI_WIREFRAMES],
    [
      { id: "quick-summary", title: "Quick Summary" },
      { id: "overview", title: "Project Overview" },
      { id: "projectlinks", title: "Project Links" },
      { id: "problem", title: "Problem Statement" },
      { id: "research", title: "User Research, Personas & Journeys" },
      { id: "design", title: "Design Process" },
      { id: "features", title: "Features & Implementation" },
      { id: "challenges", title: "Challenges & Solutions" },
      { id: "testing", title: "Testing & Quality Assurance" },
      { id: "tools", title: "Tools & Technologies Used" },
      { id: "future", title: "Future Considerations & Roadmap" },
      { id: "outcomes", title: "Outcomes & Impact" },
      { id: "conclusion", title: "Conclusion" },
    ],
    "Served as Front-End Developer on a production esports platform for a competitive gaming organization. Owned the full front-end experience,from user research and hi-fi wireframes through to React 19 implementation,while collaborating on a NestJS backend.\n\nDelivered real-time match tracking, tournament and team pages, fan-facing content, and a CMS that lets non-technical staff publish news and updates without developer support. The result is a responsive, scalable hub that centralizes teams, players, tournaments, and organizational history in one professional brand presence.",
    "Project Eversio is a production-ready esports platform built with React 19 and NestJS, serving as a central hub for teams, players, tournaments, live matches, and news, with a CMS that lets staff publish content without developer support."
  ),
};
